import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCssInline from "../styles.css?inline";
import sansFont from "@fontsource/fira-sans/files/fira-sans-latin-400-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { CONSENT_HEAD_SCRIPT } from "@/lib/consent";
import { CookieConsent } from "@/components/CookieConsent";

const GAMBETTA_STYLESHEET =
  "https://api.fontshare.com/v2/css?f[]=gambetta@600,601&display=swap";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <title>Page not found | Preserve Earth</title>
      <meta name="robots" content="noindex" />
      <div className="max-w-md text-center">
        <h1 className="font-serif text-7xl text-foreground">404</h1>
        <h2 className="mt-4 font-serif text-2xl text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-sm bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Return home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-3xl text-foreground">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-sm bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-sm border border-input bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Return home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#f5f0e8" },
      { property: "og:site_name", content: "Preserve Earth" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_GB" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@preserveearthuk" },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-48.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "preload", as: "font", type: "font/woff2", href: sansFont, crossOrigin: "anonymous" },
    ],
    // Inlined so the stylesheet never blocks first paint (mobile LCP).
    styles: [{ children: appCssInline }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["Organization", "ProfessionalService"],
              "@id": "https://preserveearth.co.uk/#org",
              name: "Preserve Earth",
              legalName: "RBM London Limited",
              url: "https://preserveearth.co.uk/",
              email: "info@preserveearth.co.uk",
              telephone: "+44 330 043 2265",
              logo: { "@type": "ImageObject", url: "https://preserveearth.co.uk/images/brand/preserve-earth-logo-navy.png" },
              image: "https://preserveearth.co.uk/images/og/preserve-earth-share.jpg",
              description:
                "UK-based provider of textile recycling, confidential waste disposal, IT & media recycling, one-off shredding and secure product destruction for businesses.",
              address: {
                "@type": "PostalAddress",
                streetAddress: "167-169 Great Portland Street, 5th Floor",
                addressLocality: "London",
                postalCode: "W1W 5PF",
                addressCountry: "GB",
              },
              areaServed: { "@type": "Country", name: "United Kingdom" },
              identifier: { "@type": "PropertyValue", propertyID: "Companies House company number", value: "15482817" },
              vatID: "GB460822305",
              sameAs: [
                "https://www.instagram.com/preserveearthuk",
                "https://www.linkedin.com/company/preserveearth",
              ],
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Services",
                itemListElement: [
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Textile Recycling" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Confidential Waste Disposal" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "IT & Media Recycling" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Secure Product Destruction" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "One-off Shredding" } },
                ],
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://preserveearth.co.uk/#website",
              url: "https://preserveearth.co.uk/",
              name: "Preserve Earth",
              publisher: { "@id": "https://preserveearth.co.uk/#org" },
              inLanguage: "en-GB",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en-GB">
      <head>
        <script dangerouslySetInnerHTML={{ __html: CONSENT_HEAD_SCRIPT }} />
        <script dangerouslySetInnerHTML={{ __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-WM9Q32JJ');` }} />
        <link rel="preconnect" href="https://api.fontshare.com" />
        <link rel="preconnect" href="https://cdn.fontshare.com" crossOrigin="anonymous" />
        <link rel="preload" as="style" href={GAMBETTA_STYLESHEET} />
        <link
          id="gambetta-css"
          rel="stylesheet"
          href={GAMBETTA_STYLESHEET}
          media="print"
          suppressHydrationWarning
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){var l=document.getElementById("gambetta-css");if(!l)return;function a(){l.media="all"}var ready=false;try{ready=!!l.sheet}catch(e){}if(ready)a();else l.onload=a})();`,
          }}
        />
        <noscript>
          <link rel="stylesheet" href={GAMBETTA_STYLESHEET} />
        </noscript>
        <HeadContent />
      </head>
      <body>
        <noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-WM9Q32JJ" height="0" width="0" hidden title="Google Tag Manager" /></noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <CookieConsent />
    </QueryClientProvider>
  );
}
